import get from 'lodash/get';
import has from 'lodash/has';
import isBoolean from 'lodash/isBoolean';
import isNumber from 'lodash/isNumber';
import {createEventHandler} from 'recompose';
import fuse from '../../../_libs/redux-fusion/index';
import {getOrganizationReduxItem} from '../../../app/model/reducers/state';
import {FormModalHOC, modalOptions} from '../../../components/modals/FormModal/index';
import {buildCreateRequestModal, buildUpdateRequestModal} from '../../../config/redux-list/requestModals';
import {getId} from '../../../core-utilities/domain/ReduxUtils';
import {getModalSubState} from '../../../core-utilities/redux-request-modal/utils';
import {createReduxDropdownList} from '../../../core-utilities/redux/ReduxDropdownListContainer';
import {applyDefaultReduxListComponentOptions} from '../../../core-utilities/redux/ReduxListContainer';
import {reduxList as formReduxList} from '../../../forms/redux/forms';
import {getFormAsString} from '../../../forms/utils/index';
import {TaskType} from '../../../tasks/enums/TaskType';
import {FileRequirementForm} from '../../components/FileRequirementForm/index';
import {FormRequirementForm} from '../../components/FormRequirementForm/index';
import {logger} from '../logger';


export function makeEditFileReqModal(config) {
  const {
    isCreate,
    taskType,
    prefix,
    getState,
    path,
  } = config;

  const EditFileRequirementFormModal = FormModalHOC(FileRequirementForm, {
    modalHeaderLabel: isCreate ? 'requirements.files.create.header.label' : 'requirements.files.edit.header.label',
  });

  const buildRequest = getFileRequestByType(taskType);
  const buildOnSave = isCreate ? makeOnSave(getState, buildRequest) : void 0;
  const builderFn = isCreate ? buildCreateRequestModal : buildUpdateRequestModal;

  return builderFn({
    prefix,
    getState,
    path,
    buildOnSave,
    modalOptions,
    modalComponent: EditFileRequirementFormModal,
    logger,
  });
}

export function makeEditFormReqModal(config) {
  const {
    isCreate,
    taskType,
    prefix,
    getState,
    path,
  } = config;

  const FormRequirementFormContainer = fuse(StreamedComponent(formReduxList))(FormRequirementForm);
  const EditFormRequirementFormModal = FormModalHOC(FormRequirementFormContainer, {
    modalHeaderLabel: isCreate ? 'requirements.forms.create.header.label' : 'requirements.forms.edit.header.label',
  });

  const buildRequest = getFormRequestByType(taskType);
  const buildOnSave = isCreate ? makeOnSave(getState, buildRequest) : void 0;
  const builderFn = isCreate ? buildCreateRequestModal : buildUpdateRequestModal;

  return builderFn({
    prefix,
    getState,
    path,
    buildOnSave,
    modalOptions,
    modalComponent: EditFormRequirementFormModal,
    logger,
  });
}

function makeOnSave(getState, buildRequest) {
  return (state$, dispatch, props$, actions) => {
    const {
      handler: onSave,
      stream: save$,
    } = createEventHandler();

    const modalState$ = state$
      .map(getState)
      .map(getModalSubState)
      .distinctUntilChanged()
      .takeUntil(props$.last());

    save$
      .withLatestFrom(modalState$)
      .takeUntil(props$.last())
      .subscribe(([data, modalState]) => {
        const request = buildRequest(data, modalState);
        dispatch(actions.sendRequest(request));
      });

    return onSave;
  };
}

function StreamedComponent(formReduxList) {
  return (state$, dispatch) => (props$) => {
    const orgId$ = state$
      .map(getOrganizationReduxItem)
      .map(itemData => itemData.id)
      .distinctUntilChanged()
      .takeUntil(props$.last());

    const propsToList$ = props$
      .combineLatest(
        orgId$,
        (props, orgId) => ({
          ...props,
          orgId,
        }));

    const componentOptions = applyDefaultReduxListComponentOptions(formReduxList.options);

    const {
      dropdownProps$: formProps$,
    } = createReduxDropdownList({
      listProp: 'formProps',
      getParentId: props => props.orgId,
      getLabel: getFormAsString,
      reduxList: formReduxList,
      options: componentOptions,
      state$,
      dispatch,
      props$: propsToList$,
    });

    return props$
      .combineLatest(
        formProps$,
        (props, formProps) => ({
          ...props,
          ...formProps,
        }));
  };
}

function getFileRequestByType(taskType) {
  switch (taskType) {
    case TaskType.ASSET:
      return getAssetFileRequirementRequest;

    case TaskType.PROJECT:
      return getProjectFileRequirementRequest;

    case TaskType.TEMPLATE:
      return getTemplateFileRequirementRequest;

  }
}

function getFormRequestByType(taskType) {
  switch (taskType) {
    case TaskType.ASSET:
      return getAssetFormRequirementRequest;

    case TaskType.PROJECT:
      return getProjectFormRequirementRequest;

    case TaskType.TEMPLATE:
      return getTemplateFormRequirementRequest;

  }
}

function getProjectFileRequirementRequest(data, modalState) {
  return {
    ...baseFileRequire(data, modalState),
  };
}

function getTemplateFileRequirementRequest(data, modalState) {
  return {
    ...baseFileRequire(data, modalState),
    templateOptions: {addToExisting: true},
  };
}

function getAssetFileRequirementRequest(data, modalState) {
  return {
    ...baseFileRequire(data, modalState),
    assetTaskId: modalState.assetTaskId,
  };
}

function baseFileRequire(data, modalState) {
  return {
    ...data,
    taskId: modalState.taskId,
    projectId: modalState.projectId,
    minimumFileCount: isNumber(data.minimumFileCount) ? data.minimumFileCount : modalState.model.minimumFileCount,
    maximumFileCount: isNumber(data.maximumFileCount) ? data.maximumFileCount : modalState.model.maximumFileCount,
    countIsEnforced: isBoolean(data.countIsEnforced) ? data.countIsEnforced : modalState.model.countIsEnforced,
  };
}

function getProjectFormRequirementRequest(data, modalState) {
  return {
    ...baseFormRequire(data, modalState),
  };
}

function getTemplateFormRequirementRequest(data, modalState) {
  return {
    ...baseFormRequire(data, modalState),
    templateOptions: {addToExisting: true},
  };
}

function getAssetFormRequirementRequest(data, modalState) {
  return {
    ...baseFormRequire(data, modalState),
    assetTaskId: modalState.assetTaskId,
  };
}

function baseFormRequire(data, modalState) {
  return {
    ...data,
    form: void 0,
    taskId: modalState.taskId,
    projectId: modalState.projectId,
    formId: has(data, 'form') ? getId(data.form) : getId(get(modalState, 'model.form')),
  };
}



// WEBPACK FOOTER //
// ./src/js/requirements/redux/config/configEditReqRequests.js